import { FC, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../../app/store/store";
import { IStudentTag, studentService } from "../../../../entities/student";
import { ITutorData, tutorService } from "../../../../entities/tutor";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { useGlobalLoadingActions } from "../../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { SearchItems } from "../../../../features/searchItems";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { MyError } from "../../../../shared/lib/error/MyError";
import { MyButton } from "../../../../shared/ui/button";
import { MyCheckbox } from "../../../../shared/ui/myCheckbox/myCheckbox";
import { Modal } from "../../../../shared/ui/modal";
import classes from './tutorTags.module.scss'

interface IProps {
    tutor: ITutorData;
    onTagsChange: (tagIds: number[]) => void;
}

const getTagName = (tag: IStudentTag) => tag.tag_name || `Тег #${tag.id}`

export const TutorTags: FC<IProps> = ({tutor, onTagsChange}) => {
    const [open, setOpen] = useState(false)
    const [allTags, setAllTags] = useState<IStudentTag[]>([])
    const [allTagsSearch, setAllTagsSearch] = useState<Array<IStudentTag & {name: string}>>([])
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>(tutor.tag_ids || [])
    const [isLoading, setIsLoading] = useState(false)
    const {my} = useAppSelector(state => state.myReducer)
    const {setIsLoading: setGlobalLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const tutorUserId = Number(tutor.user_id || tutor.id)
    const canAssignTags = hasFeature(my, FEATURES.IDM.STAFF_TAGS_ASSIGN, tutorUserId)
    // Новые точечные ручки тегов защищены отдельной фичей и не требуют
    // права на редактирование всей карточки исполнителя.
    const canEditTags = canAssignTags

    useEffect(() => {
        setSelectedTagIds(tutor.tag_ids || [])
    }, [tutor.tag_ids])

    const selectedTags = useMemo(() => {
        return (tutor.tag_ids || [])
            .map(id => allTags.find(tag => tag.id === id) || {
                id,
                tag_name: `Тег #${id}`,
                students_count: 0,
                tutors_count: 0,
            })
            .sort((first, second) => getTagName(first).localeCompare(getTagName(second)))
    }, [allTags, tutor.tag_ids])

    const handleError = (error: unknown, fallback: string) => {
        console.log(error)
        if(error instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: error.message, type: 'error'})
            return
        }
        if(error instanceof MyError){
            setGlobalMessage({message: error.message, type: 'error'})
            return
        }
        setGlobalMessage({message: fallback, type: 'error'})
    }

    const loadTags = async (reportError = true) => {
        try{
            setIsLoading(true)
            const tags = await studentService.getTags()
            setAllTags(tags)
            setAllTagsSearch(tags.map(tag => ({...tag, name: getTagName(tag)})))
        }
        catch(error){
            if(reportError){
                handleError(error, 'Ошибка при получении списка тегов')
            }
            else{
                console.log(error)
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if((tutor.tag_ids || []).length > 0){
            void loadTags(false)
        }
        // The catalogue is shared with student tags and is needed to display tag names.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tutor.tag_ids])

    const updateTags = async (tagIds: number[]) => {
        const currentTagIds = tutor.tag_ids || []
        const tagIdsToAdd = tagIds.filter(tagId => !currentTagIds.includes(tagId))
        const tagIdsToRemove = currentTagIds.filter(tagId => !tagIds.includes(tagId))

        await Promise.all([
            ...tagIdsToAdd.map(tagId => tutorService.addTagToTutor(tutorUserId, tagId)),
            ...tagIdsToRemove.map(tagId => tutorService.removeTagFromTutor(tutorUserId, tagId)),
        ])
        onTagsChange(tagIds)
    }

    const onOpen = async () => {
        if(!canEditTags){
            return
        }
        setSelectedTagIds(tutor.tag_ids || [])
        setOpen(true)
        if(allTags.length === 0){
            await loadTags()
        }
    }

    const onSelected = (tagId: number) => (selected: boolean) => {
        setSelectedTagIds(current => selected
            ? current.includes(tagId) ? current : [...current, tagId]
            : current.filter(id => id !== tagId)
        )
    }

    const onRemove = async (tagId: number) => {
        if(!canEditTags){
            return
        }
        try{
            setGlobalLoading(true)
            const nextTagIds = (tutor.tag_ids || []).filter(id => id !== tagId)
            await updateTags(nextTagIds)
            setGlobalMessage({message: 'Тег удален у исполнителя', type: 'ok'})
        }
        catch(error){
            handleError(error, 'Ошибка при удалении тега у исполнителя')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    const onSave = async () => {
        try{
            setGlobalLoading(true)
            await updateTags(selectedTagIds)
            setOpen(false)
            setGlobalMessage({message: 'Теги исполнителя обновлены', type: 'ok'})
        }
        catch(error){
            handleError(error, 'Ошибка при обновлении тегов исполнителя')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <span className={classes.title}>
                    <svg className={classes.titleIcon} viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5V12L12 20L20 12L13 4H4V5Z" /><circle cx="8" cy="8" r="1" /></svg>
                    Теги исполнителя
                </span>
                {canEditTags && <button type="button" className={classes.add} onClick={onOpen} aria-label="Изменить теги">+</button>}
            </section>
            <section className={classes.tags}>
                {selectedTags.length > 0
                    ? selectedTags.map(tag => (
                        <section key={tag.id} className={classes.tag}>
                            <span>{getTagName(tag)}</span>
                            {canEditTags && <button type="button" className={classes.remove} onClick={() => onRemove(tag.id)} aria-label={`Удалить тег ${getTagName(tag)}`}>×</button>}
                        </section>
                    ))
                    : <span className={classes.empty}>Теги пока не назначены</span>}
            </section>
            <Modal open={open} setOpen={setOpen}>
                <section className={classes.modal}>
                    <section className={classes.modalTitle}>Выберите теги исполнителя</section>
                    <section className={classes.search}>
                        <SearchItems
                            placeholder="Введите название тега"
                            items={allTags.map(tag => ({...tag, name: getTagName(tag)}))}
                            setItems={setAllTagsSearch}
                        />
                    </section>
                    <section className={classes.modalList}>
                        {isLoading
                            ? <span className={classes.empty}>Загрузка...</span>
                            : allTagsSearch.length > 0
                                ? allTagsSearch.slice().sort((a, b) => getTagName(a).localeCompare(getTagName(b))).map(tag => (
                                    <section key={tag.id} className={classes.checkbox}>
                                        <MyCheckbox checked={selectedTagIds.includes(tag.id)} onSelected={onSelected(tag.id)} label={getTagName(tag)} />
                                    </section>
                                ))
                                : <span className={classes.empty}>Список тегов пуст</span>}
                    </section>
                    <section className={classes.actions}><MyButton onClick={onSave} isLoading={isLoading}>Сохранить</MyButton></section>
                </section>
            </Modal>
        </section>
    )
}
